import { SIDE_IMAGE_MEASUREMENTS } from "./imageMeasurements";
import { SIDE_ANGLES } from "./models";
import { Euler, Matrix4, Quaternion, Vector3 } from "three";

const DEFAULT_DECAL_SURFACE = {
  widthScale: 1,
  heightScale: 1,
  offsetX: 0,
  offsetY: 0,
  preserveDecalAspect: false,
  horizontalOnlySnapAbove: null,
  upperSnapOffsetY: 0,
  snapAcrossSideMeshes: false,
  projectAcrossSideMeshes: false,
  filterOutwardFacingTriangles: false,
  targetMeshNamePrefixes: null,
  excludedTargetMeshNamePrefixes: null,
  acceptReversedOutwardNormals: false,
  doubleSidedSurfaceRaycast: false,
  decalDepth: null,
};

export const SIDE_DECAL_PROJECTORS = {
  front: {
    normalAxis: "z",
    normalSign: 1,
    orientation: new Euler(0, 0, 0),
  },
  back: {
    normalAxis: "z",
    normalSign: -1,
    orientation: new Euler(0, Math.PI, 0),
    flipX: true,
  },
  left: {
    normalAxis: "x",
    normalSign: -1,
    orientation: new Euler(0, -Math.PI / 2, 0),
  },
  right: {
    normalAxis: "x",
    normalSign: 1,
    orientation: new Euler(0, Math.PI / 2, 0),
    flipX: true,
  },
};

const BACKPACK_MODEL_NAMES = new Set(["Backpack_Black", "Backpack_Brown"]);
const TOILETRY_MODEL_NAMES = new Set(["Toiletry_Black", "Toiletry_Brown"]);
const KEYCHAIN_MODEL_NAMES = new Set(["Keychain_Black", "Keychain_Brown"]);
const CURVED_SURFACE_SNAP_TO_SURFACE_SIDES = {
  Backpack_Black: new Set(["front", "back", "left", "right", "top"]),
  Backpack_Brown: new Set(["front", "back", "left", "right", "top"]),
  Large_Weekender_Black: new Set(["front", "back", "left", "right", "top"]),
  Large_Weekender_Brown: new Set(["front", "back", "left", "right", "top"]),
  Small_Weekender_Black: new Set(["front", "back", "top"]),
  Small_Weekender_Brown: new Set(["front", "back", "top"]),
  Toiletry_Black: new Set(["front", "back", "left", "right", "top"]),
  Toiletry_Brown: new Set(["front", "back", "left", "right", "top"]),
  Zippered_Black: new Set(["front", "back", "left", "right", "top"]),
  Zippered_Brown: new Set(["front", "back", "left", "right", "top"]),
};

const normalizeAngle = (angle) => {
  let normalized = angle % (Math.PI * 2);
  if (normalized > Math.PI) normalized -= Math.PI * 2;
  if (normalized < -Math.PI) normalized += Math.PI * 2;
  return normalized;
};

const anglesMatch = (a, b) =>
  Math.abs(normalizeAngle(a - b)) < 0.001;

export const getPhysicalDecalSide = (selectedModel, side) => {
  if (side === "top") {
    return side;
  }

  if (!BACKPACK_MODEL_NAMES.has(selectedModel.name) && !TOILETRY_MODEL_NAMES.has(selectedModel.name)) {
    return side;
  }

  const sideAngle = selectedModel.sideAngles?.[side];
  if (sideAngle === undefined) {
    return side;
  }

  return Object.entries(SIDE_ANGLES).find(([, defaultAngle]) =>
    anglesMatch(sideAngle, defaultAngle)
  )?.[0] ?? side;
};

const getTopDecalProjector = (selectedModel) => {
  const physicalFront = getPhysicalDecalSide(selectedModel, "front");
  const frontProjector = SIDE_DECAL_PROJECTORS[physicalFront];
  const outwardNormal = new Vector3(0, 1, 0);
  const frontNormal = new Vector3();
  frontNormal[frontProjector.normalAxis] = frontProjector.normalSign;
  const screenUp = frontNormal.clone().negate();
  const screenRight = new Vector3().crossVectors(outwardNormal, frontNormal);
  const horizontalAxis = Math.abs(screenRight.x) > 0.5 ? "x" : "z";
  const orientationMatrix = new Matrix4().makeBasis(
    screenRight,
    screenUp,
    outwardNormal
  );

  return {
    normalAxis: "y",
    normalSign: 1,
    horizontalAxis,
    verticalAxis: frontProjector.normalAxis,
    verticalSign: -frontProjector.normalSign,
    orientation: new Euler().setFromRotationMatrix(orientationMatrix),
    flipX: screenRight[horizontalAxis] < 0,
  };
};

const KEYCHAIN_DECAL_PROJECTORS = {
  front: {
    normalAxis: "y",
    normalSign: 1,
    horizontalAxis: "x",
    verticalAxis: "z",
    verticalSign: -1,
    orientation: new Euler(-Math.PI / 2, 0, 0),
  },
  back: {
    normalAxis: "y",
    normalSign: -1,
    horizontalAxis: "x",
    verticalAxis: "z",
    verticalSign: 1,
    orientation: new Euler(Math.PI / 2, 0, 0),
  },
};

export const getSideDecalProjector = (selectedModel, side) =>
  side === "top"
    ? getTopDecalProjector(selectedModel)
    : KEYCHAIN_MODEL_NAMES.has(selectedModel.name)
      ? KEYCHAIN_DECAL_PROJECTORS[side]
      : SIDE_DECAL_PROJECTORS[getPhysicalDecalSide(selectedModel, side)];

export const shouldSnapDecalToSurface = (selectedModel, side) =>
  KEYCHAIN_MODEL_NAMES.has(selectedModel.name) ||
  CURVED_SURFACE_SNAP_TO_SURFACE_SIDES[selectedModel.name]?.has(side);

export const getDecalOrientation = (projector, angleRadians = 0) => {
  const normal = new Vector3();
  normal[projector.normalAxis] = projector.normalSign;

  const baseRotation = new Quaternion().setFromEuler(projector.orientation);
  // Fabric's positive angles are clockwise in its Y-down canvas. Rotate in
  // the opposite direction around the outward face normal to preserve that
  // same apparent direction on every 3D face.
  const brandRotation = new Quaternion().setFromAxisAngle(normal, -angleRadians);
  return new Euler().setFromQuaternion(brandRotation.multiply(baseRotation));
};

const weekenderFront     = { left: 122, top: 162, width: 1542, height: 714, widthScale: 0.9, heightScale: 0.55, offsetY: -0.24, }
const smallWeekenderLeft = { left: 90,  top: 138, width: 700,  height: 720,  widthScale: 0.85, heightScale: 0.5, offsetY: -0.22 }
const smallWeekenderFrontDecals = { heightScale: 0.5, offsetY: -0.22,}
// Surface ratios below are measured from the leather body mesh relative to
// each complete model (which also contains handles, straps, and hardware).
// Keeping position mapping tied to the body while preserving decal aspect
// lets edge placements reach curved seams without stretching the brand.
const largeWeekenderFront = {
  ...weekenderFront,
  // The brandable front/back panel begins at the upper leather seam rather
  // than below the handle anchors. Mapping the full panel keeps positions
  // aligned with the fixed logo and prevents upper corners from wrapping onto
  // the top face.
  left: 100,
  top: 60,
  width: 1600,
  height: 780,
  widthScale: 0.927,
  heightScale: 0.591,
  offsetX: 0.001,
  offsetY: -0.181,
  preserveDecalAspect: true,
  filterOutwardFacingTriangles: true,
  horizontalOnlySnapAbove: 0.2,
  upperSnapOffsetY: -0.03,
};
const largeWeekenderLeft = {
  left: 25,
  top: 15,
  width: 830,
  height: 865,
  widthScale: 1,
  heightScale: 0.591,
  offsetY: -0.181,
  preserveDecalAspect: true,
};
const largeWeekenderTop = {
  left: 110,
  top: 21,
  width: 1585,
  height: 858,
  widthScale: 0.927,
  heightScale: 1,
  offsetX: 0.001,
  offsetY: 0,
  preserveDecalAspect: true,
};
const smallWeekenderTop = {
  left: 107,
  top: 21,
  width: 1591,
  height: 858,
  widthScale: 0.927,
  heightScale: 1,
  offsetX: 0.001,
  offsetY: 0,
  preserveDecalAspect: true,
};

const backpackFace = { left: 622, top: 62, width: 550, height: 775, widthScale: 0.878, heightScale: 0.922, offsetY: -0.003, preserveDecalAspect: true };
// The raised front pocket sits in front of the main shell. Project front marks
// onto the nearest visible leather mesh so pocket brands are not hidden behind
// it, while retaining the existing single-shell behavior on the back.
const backpackFront = { ...backpackFace, snapAcrossSideMeshes: true };
const backpackBack = backpackFace;
const backpackSideSurface = {
  top: 70,
  width: 280,
  height: 760,
  widthScale: 0.654,
  heightScale: 0.922,
  offsetX: 0.174,
  offsetY: -0.003,
  preserveDecalAspect: true,
  horizontalOnlySnapAbove: 1,
  snapAcrossSideMeshes: true,
};
const backpackLeft = { left: 381, ...backpackSideSurface };
const backpackRight = { left: 238, ...backpackSideSurface };
const backpackTop = {
  left: 391,
  top: 319,
  width: 1018,
  height: 448,
  widthScale: 0.878,
  heightScale: 0.522,
  offsetX: 0,
  offsetY: 0.108,
  preserveDecalAspect: true,
};

// The transparent toiletry references include zipper pulls and attachment
// tabs beyond the brandable leather. Use the panel silhouette itself so an
// equal inset in the 2D editor remains equal on both sides of the 3D model.
const toiletrySurface = {
  widthScale: 0.887,
  heightScale: 0.983,
  offsetX: -0.033,
  offsetY: 0.009,
  preserveDecalAspect: true,
  horizontalOnlySnapAbove: 1,
};
const toiletryFront = { left: 195, top: 79, width: 1522, height: 742, ...toiletrySurface };
const toiletryBack = { left: 83, top: 79, width: 1522, height: 742, ...toiletrySurface };
const toiletryLeft = {
  left: 67,
  top: 64,
  width: 810,
  height: 773,
  ...toiletrySurface,
  widthScale: 0.946,
  offsetX: -0.027,
};
const toiletryRight = { ...toiletryLeft, left: 22 };
const toiletryTop = {
  left: 194,
  top: 82,
  width: 1522,
  height: 779,
  widthScale: 0.887,
  heightScale: 0.976,
  offsetX: -0.032,
  offsetY: -0.012,
  preserveDecalAspect: true,
};

const zipperedFront = { widthScale: 0.991, heightScale: 0.585, offsetY: -0.208, preserveDecalAspect: true, horizontalOnlySnapAbove: 0.2, upperSnapOffsetY: 0.012 };
// The tote body occupies 58.5% of the full model height once its handles are
// included in the model bounds. Its measured center offset maps the complete
// 2D leather panel from the upper rim to the bottom seam.
const zipperedLeft = {
  widthScale: 0.996,
  heightScale: 0.585,
  offsetX: -0.002,
  offsetY: -0.208,
  preserveDecalAspect: true,
  horizontalOnlySnapAbove: 0.2,
  upperSnapOffsetY: 0.012,
};
const zipperedTop = {
  widthScale: 0.991,
  heightScale: 0.96,
  offsetX: 0,
  offsetY: 0,
  preserveDecalAspect: true,
  snapAcrossSideMeshes: true,
  projectAcrossSideMeshes: true,
  // The exported Top is assembled from multiple leather meshes, while the
  // handles are the model's highest geometry. Raycast against every non-handle
  // mesh so the leather directly beneath each 2D point wins deterministically.
  excludedTargetMeshNamePrefixes: ["pPlane7", "pPlane8"],
  // Some leather pieces were exported with reversed vertex normals. Accept
  // either winding, but still reject vertical/front-facing triangles.
  acceptReversedOutwardNormals: true,
  doubleSidedSurfaceRaycast: true,
  decalDepth: 0.14,
};

// The keychain reference includes its ring and strap, but brands can only be
// placed on the rectangular leather tab. These bounds and surface ratios map
// that tab directly to the matching X-Z face in the model.
const keychainFront = { left: 641, top: 304, width: 1116, height: 478, widthScale: 0.6514, heightScale: 0.6361, offsetX: 0.1743, offsetY: 0.1226 };
const keychainBack = { ...keychainFront, top: 119 };

const MODEL_SOURCE_BOUNDS = {
  Keychain_Black: {
    front: keychainFront,
    back: keychainBack,
  },
  Keychain_Brown: {
    front: keychainFront,
    back: keychainBack,
  },
  Large_Weekender_Black: {
    ...SIDE_IMAGE_MEASUREMENTS["Large_Weekender_Black"],
    front: largeWeekenderFront,
    back: largeWeekenderFront,
    left: largeWeekenderLeft,
    right: largeWeekenderLeft,
    top: largeWeekenderTop,
  },
  Large_Weekender_Brown: {
    ...SIDE_IMAGE_MEASUREMENTS["Large_Weekender_Brown"],
    front: largeWeekenderFront,
    back: largeWeekenderFront,
    left: largeWeekenderLeft,
    right: largeWeekenderLeft,
    top: largeWeekenderTop,
  },
  Small_Weekender_Brown: {
    ...SIDE_IMAGE_MEASUREMENTS["Small_Weekender_Black"],
    front: {...weekenderFront, ...smallWeekenderFrontDecals },
    back: {...weekenderFront, ...smallWeekenderFrontDecals }, 
    left: smallWeekenderLeft, 
    right: smallWeekenderLeft,
    top: smallWeekenderTop,
  },
  Small_Weekender_Black: {
    ...SIDE_IMAGE_MEASUREMENTS["Large_Weekender_Black"],
    front: {...weekenderFront, ...smallWeekenderFrontDecals },
    back: {...weekenderFront, ...smallWeekenderFrontDecals }, 
    left: smallWeekenderLeft, 
    right: smallWeekenderLeft,
    top: smallWeekenderTop,
  },
  Backpack_Black: {
    ...SIDE_IMAGE_MEASUREMENTS["Backpack_Black"],
    front: backpackFront,
    back: backpackBack,
    left: backpackLeft, 
    right: backpackRight,
    top: backpackTop,
  },
  Backpack_Brown: {
    ...SIDE_IMAGE_MEASUREMENTS["Backpack_Black"],
    front: backpackFront,
    back: backpackBack,
    left: backpackLeft, 
    right: backpackRight,
    top: backpackTop,
  }, 
  Toiletry_Black: {
    ...SIDE_IMAGE_MEASUREMENTS["Toiletry_Black"],
    front: toiletryFront,
    back: toiletryBack,
    left: toiletryLeft,
    right: toiletryRight,
    top: toiletryTop,
  },
  Toiletry_Brown: {
    ...SIDE_IMAGE_MEASUREMENTS["Toiletry_Brown"],
    front: toiletryFront,
    back: toiletryBack,
    left: toiletryLeft,
    right: toiletryRight,
    top: toiletryTop,
  },
  Zippered_Black: {
    ...SIDE_IMAGE_MEASUREMENTS["Zippered_Black"],
    front: zipperedFront,
    back: zipperedFront,
    left: zipperedLeft,
    right: zipperedLeft,
    top: zipperedTop,
  },
  Zippered_Brown: {
    ...SIDE_IMAGE_MEASUREMENTS["Zippered_Brown"],
    front: zipperedFront,
    back: zipperedFront,
    left: zipperedLeft,
    right: zipperedLeft,
    top: zipperedTop,
  }
};


export const getSourceBounds = (modelName, side, measurements) => ({
  ...{
    left: measurements.left,
    top: measurements.top,
    width: measurements.contentWidth,
    height: measurements.contentHeight,
  },
  ...MODEL_SOURCE_BOUNDS[modelName]?.[side],
});


const SIDE_IMAGE_SCALE = 3;
export const getSideSourceBounds = (selectedModel, side) => {
  const measurements = SIDE_IMAGE_MEASUREMENTS[selectedModel.name][side];
  const rawSourceBounds = getSourceBounds(selectedModel.name, side, measurements);
  return {
    left: rawSourceBounds.left / SIDE_IMAGE_SCALE,
    top: rawSourceBounds.top / SIDE_IMAGE_SCALE,
    width: rawSourceBounds.width / SIDE_IMAGE_SCALE,
    height: rawSourceBounds.height / SIDE_IMAGE_SCALE,
    heightScale: rawSourceBounds.heightScale ?? DEFAULT_DECAL_SURFACE.heightScale,  
    widthScale: rawSourceBounds.widthScale ?? DEFAULT_DECAL_SURFACE.widthScale,
    offsetY: rawSourceBounds.offsetY ?? DEFAULT_DECAL_SURFACE.offsetY,
    offsetX: rawSourceBounds.offsetX ?? DEFAULT_DECAL_SURFACE.offsetX,
    preserveDecalAspect:
      rawSourceBounds.preserveDecalAspect ?? DEFAULT_DECAL_SURFACE.preserveDecalAspect,
    horizontalOnlySnapAbove:
      rawSourceBounds.horizontalOnlySnapAbove ?? DEFAULT_DECAL_SURFACE.horizontalOnlySnapAbove,
    upperSnapOffsetY:
      rawSourceBounds.upperSnapOffsetY ?? DEFAULT_DECAL_SURFACE.upperSnapOffsetY,
    snapAcrossSideMeshes:
      rawSourceBounds.snapAcrossSideMeshes ?? DEFAULT_DECAL_SURFACE.snapAcrossSideMeshes,
    projectAcrossSideMeshes:
      rawSourceBounds.projectAcrossSideMeshes ?? DEFAULT_DECAL_SURFACE.projectAcrossSideMeshes,
    filterOutwardFacingTriangles:
      rawSourceBounds.filterOutwardFacingTriangles ?? DEFAULT_DECAL_SURFACE.filterOutwardFacingTriangles,
    targetMeshNamePrefixes:
      rawSourceBounds.targetMeshNamePrefixes ?? DEFAULT_DECAL_SURFACE.targetMeshNamePrefixes,
    excludedTargetMeshNamePrefixes:
      rawSourceBounds.excludedTargetMeshNamePrefixes ?? DEFAULT_DECAL_SURFACE.excludedTargetMeshNamePrefixes,
    acceptReversedOutwardNormals:
      rawSourceBounds.acceptReversedOutwardNormals ?? DEFAULT_DECAL_SURFACE.acceptReversedOutwardNormals,
    doubleSidedSurfaceRaycast:
      rawSourceBounds.doubleSidedSurfaceRaycast ?? DEFAULT_DECAL_SURFACE.doubleSidedSurfaceRaycast,
    decalDepth:
      rawSourceBounds.decalDepth ?? DEFAULT_DECAL_SURFACE.decalDepth,
  };
};

export const getDecalDimensions = ({
  objectHeight,
  objectScaleX,
  objectScaleY,
  objectWidth,
  sourceBounds,
  surfaceHeight,
  surfaceWidth,
}) => {
  const horizontalPixelsToModel = surfaceWidth / sourceBounds.width;
  const verticalPixelsToModel = sourceBounds.preserveDecalAspect
    ? horizontalPixelsToModel
    : surfaceHeight / sourceBounds.height;

  return {
    width: objectWidth * objectScaleX * horizontalPixelsToModel,
    height: objectHeight * objectScaleY * verticalPixelsToModel,
  };
};
